import React, { useState, useEffect, useCallback } from "react";
import { http } from "../api";
import { toast } from "sonner";
import { LogOut, Package } from "lucide-react";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("co_admin_token") || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadOrders = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/orders", { headers: { Authorization: `Bearer ${tk}` } });
      setOrders(data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("co_admin_token"); setToken(""); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) loadOrders(token); }, [token, loadOrders]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/auth/login", creds);
      localStorage.setItem("co_admin_token", data.token);
      setToken(data.token);
      toast.success("Connecté");
    } catch (err) {
      toast.error("Identifiants invalides");
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem("co_admin_token"); setToken(""); setOrders([]); };

  if (!token)
    return (
      <div className="max-w-sm mx-auto px-6 py-28">
        <h1 className="serif text-4xl mb-2">Admin</h1>
        <p className="text-sm text-[#737373] mb-8">ClassOne Vintage — Espace privé</p>
        <form onSubmit={login} className="space-y-6">
          <input required type="email" data-testid="admin-email" placeholder="Courriel" value={creds.email}
            onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            className="w-full bg-transparent border-b border-[#1A1A1A]/25 py-3 outline-none focus:border-[#1A1A1A]" />
          <input required type="password" data-testid="admin-password" placeholder="Mot de passe" value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            className="w-full bg-transparent border-b border-[#1A1A1A]/25 py-3 outline-none focus:border-[#1A1A1A]" />
          <button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="w-full bg-[#1A1A1A] text-[#F9F8F6] py-3.5 text-sm uppercase tracking-widest rounded-sm disabled:opacity-60">
            {loading ? "…" : "Se connecter"}
          </button>
        </form>
      </div>
    );

  const paid = orders.filter((o) => o.payment_status === "paid");
  const revenue = paid.reduce((a, o) => a + o.amount, 0);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="serif text-5xl">Commandes</h1>
        <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#1A1A1A]">
          <LogOut size={16} strokeWidth={1.5} /> Déconnexion
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
        <div className="bg-[#F0EBE1] p-5 rounded-sm"><p className="text-xs uppercase tracking-widest text-[#737373]">Commandes payées</p><p className="serif text-3xl mt-1">{paid.length}</p></div>
        <div className="bg-[#F0EBE1] p-5 rounded-sm"><p className="text-xs uppercase tracking-widest text-[#737373]">Revenu</p><p className="serif text-3xl mt-1">{revenue.toFixed(2)} $</p></div>
        <div className="bg-[#F0EBE1] p-5 rounded-sm"><p className="text-xs uppercase tracking-widest text-[#737373]">Total (toutes)</p><p className="serif text-3xl mt-1">{orders.length}</p></div>
      </div>

      {loading && <p className="text-sm text-[#737373]">Chargement…</p>}
      {!loading && orders.length === 0 && (
        <div className="text-center py-20 text-[#737373]"><Package size={40} strokeWidth={1} className="mx-auto mb-4" /><p>Aucune commande pour l'instant.</p></div>
      )}

      <div className="space-y-4" data-testid="orders-list">
        {orders.map((o) => (
          <div key={o.id} data-testid={`order-${o.id}`} className="border border-[#1A1A1A]/10 rounded-sm p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <span className="serif text-lg">{o.customer?.name}</span>
                <span className="text-sm text-[#737373]"> · {o.customer?.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="serif text-lg">{o.amount.toFixed(2)} $ CAD</span>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${o.payment_status === "paid" ? "bg-[#1A1A1A] text-[#F9F8F6]" : "bg-[#E8E2D2] text-[#737373]"}`}>
                  {o.payment_status === "paid" ? "Payée" : "En attente"}
                </span>
              </div>
            </div>
            <p className="text-sm text-[#404040]">
              {o.items?.map((it) => `${it.name}${it.size ? " - " + it.size : ""} ×${it.qty}`).join(" · ")}
            </p>
            <p className="text-xs text-[#737373] mt-2">
              {o.customer?.address}, {o.customer?.city}, {o.customer?.province} {o.customer?.postal_code}, {o.customer?.country}
              {o.customer?.phone ? ` · ${o.customer.phone}` : ""}
            </p>
            <p className="text-[11px] text-[#737373] mt-1">{new Date(o.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
